'use client';
import { API_ROUTES } from '@shared/api-utils';
import { useFetchGames } from '@shared/hooks';
import { Vip } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { type ComponentProps, useEffect, useRef } from 'react';
import type { GamesCategoryFilter } from '../../games/types/types';
import { Carousel, JackpotContainer, JackpotHeader, Jackpots, Title } from '../components';
import { useFetchJackpots } from '../hooks';
import type { JackpotBonanzaApiResponse, JackpotBonanzaJackpots, JackpotType, JackpotVariant } from '../types';

const TRANSLATIONS_NAMESPACE = 'casino.jackpot_bonanza';
const JACKPOT_REFRESH_INTERVAL = 180_000;
const BONANZA_LOGO = 'https://media.ffycdn.net/eu/lottoland/ii2d38dwFzsbFgfgkbBh.png';

type JackpotBonanzaJackpotsData = Record<JackpotBonanzaJackpots, number>;
type JackpotsBonanzaCurrentAndPrevious = Record<
  JackpotBonanzaJackpots,
  {
    current: number;
    previous: number;
  }
>;
type JackpotBonanzaProps = ComponentProps<'div'> & {
  variant?: JackpotVariant;
  hideCarousel?: boolean;
  gamesFilter: GamesCategoryFilter;
};

/**
 * Gets the current and previous jackpot values for each jackpot key.
 * In case of the first time load, when previous is not available, we use an initial value adjustment to allow the animation to start.
 * @param current - The current jackpot values
 * @param previous - The previous jackpot values
 * @returns The current and previous jackpot values for each jackpot key
 */
function getJackpots(current: JackpotBonanzaJackpotsData | undefined, previous: JackpotBonanzaJackpotsData | null): JackpotsBonanzaCurrentAndPrevious | null {
  if (!current) return null;
  const initValueAdjustment: JackpotBonanzaJackpotsData = { grand: 1, mega: 0.5, major: 0.25, mini: 0.01 };
  return (Object.keys(current) as JackpotBonanzaJackpots[]).reduce((acc, key) => {
    const isCurrentHigherThanPrevious = current[key] > (previous?.[key] ?? 0);
    acc[key] = {
      current: current[key],
      previous: previous?.[key] && isCurrentHigherThanPrevious ? previous[key] : current[key] - initValueAdjustment[key],
    };
    return acc;
  }, {} as JackpotsBonanzaCurrentAndPrevious);
}

/**
 * Maps a jackpot data to a JackpotType object, used to render the jackpot info component.
 * @param key - The jackpot key (grand, mega, major, mini)
 * @param current - The current jackpot value
 * @param previous - The previous jackpot value
 * @param t - The translations function
 * @param refreshInterval - The refresh interval
 * @param variant - The variant of the jackpot bonanza section (default or sticky)
 * @returns The jackpot object
 */
function mapJackpot(key: string, current: number, previous: number, t: ReturnType<typeof useTranslations>, refreshInterval: number, variant: JackpotVariant): JackpotType {
  return {
    label: t(key),
    value: current * 100,
    previousValue: previous * 100,
    refreshInterval,
    highlight: key === 'grand',
    icon: key === 'grand' ? <Vip size="medium" /> : undefined,
    variant,
  };
}

/**
 * JackpotBonanza component displays the jackpot bonanza section.
 * @param variant - The variant of the jackpot bonanza section (default or sticky)
 * @param hideCarousel - Whether to hide the carousel (for example, when used inside category page)
 * @returns The JackpotBonanza component
 */
function JackpotBonanza({ variant = 'default', className, hideCarousel = false, gamesFilter }: JackpotBonanzaProps) {
  const t = useTranslations(TRANSLATIONS_NAMESPACE);
  const { currency } = useClientConfig();
  const { data } = useFetchJackpots<JackpotBonanzaApiResponse>(API_ROUTES.GAMES.JACKPOT_BONANZA.replace(':currency', currency), JACKPOT_REFRESH_INTERVAL);
  const previousJackpotsRef = useRef<JackpotBonanzaJackpotsData | null>(null);
  const { games } = useFetchGames({ ...gamesFilter, isLoggedIn: false });

  useEffect(() => {
    if (data) {
      previousJackpotsRef.current = data.jackpots;
    }
  }, [data]);

  // React refs are values that are not needed for rendering. Refs should only be accessed outside of render, such as in event handlers or effects. Accessing a ref value (the
  // `current` property) during render can cause your component not to update as expected (https://react.dev/reference/react/useRef).
  // eslint-disable-next-line react-hooks/refs
  const jackpots = getJackpots(data?.jackpots, previousJackpotsRef?.current);
  const mappedJackpots = jackpots ? Object.entries(jackpots).map(([key, value]) => mapJackpot(key, value.current, value.previous, t, JACKPOT_REFRESH_INTERVAL, variant)) : [];

  const filteredGames = games?.filter(game => data?.games.includes(game.name)) ?? [];

  return (
    <JackpotContainer variant={variant} className={className} data-track-container="Jackpot Bonanza" data-testid="jackpot-bonanza-section">
      <JackpotHeader variant={variant} className="gap-y-2">
        <Title variant={variant} namespace={TRANSLATIONS_NAMESPACE} className="text-accent-orange sr-only" />
        <Image
          src={BONANZA_LOGO}
          alt="Jackpot Big Bass Bonanza"
          unoptimized
          width={438}
          height={228}
          className={twMerge('mx-auto object-contain', variant === 'default' ? 'w-100' : 'w-50')}
        />
        <Jackpots layout="bonanza" jackpots={mappedJackpots} variant={variant} />
      </JackpotHeader>
      {variant === 'default' && !hideCarousel ? <Carousel namespace={TRANSLATIONS_NAMESPACE} games={filteredGames} /> : null}
    </JackpotContainer>
  );
}

export { getJackpots, JackpotBonanza, mapJackpot };
